import { EditorialNavigationLayout } from "./site/EditorialNavigationLayout";
import { ReactNode } from "react";
import { MainNav } from "./nav/MainNav";
import { Footer } from "./nav/Footer";
import { Helmet } from "react-helmet-async";
import { CookieConsent } from "./CookieConsent";
import { AskCaireLauncher } from "./marketing/home/AskCaireLauncher";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "@/config/env";
import { AnnouncementBar } from "./marketing/AnnouncementBar";
import { useAnalysisAcquisition } from "@/hooks/useAnalysisAcquisition";
import { useKeyboardScrollRegions } from "@/hooks/useKeyboardScrollRegions";

interface LayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  meta?: {
    title?: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    twitterTitle?: string;
    twitterDescription?: string;
  };
}

/**
 * Layout component that wraps all pages
 * Includes the head metadata, navigation, and footer
 */
const Layout = ({ children, title, description, meta }: LayoutProps) => {
  const { t } = useTranslation(["common", "layout"]);
  const location = useLocation();
  useAnalysisAcquisition();
  useKeyboardScrollRegions(
    location.pathname,
    location.pathname.startsWith("/en")
      ? "Scrollable content"
      : "Rullbart innehåll",
  );

  // Default values from translations or fallbacks
  const defaultTitle = t(
    "layout.title",
    "Caire - AI-driven scheduling for home care",
  );
  const defaultDescription = t(
    "layout.description",
    "Optimize your home care operations with intelligent scheduling that puts wellbeing first.",
  );

  // Determine final values, with priority: direct props > meta props > translations > hardcoded defaults
  const pageTitle = title || meta?.title || defaultTitle;
  const pageDescription =
    description || meta?.description || defaultDescription;

  // Determine open graph and twitter values
  const ogTitle = meta?.ogTitle || pageTitle;
  const ogDescription = meta?.ogDescription || pageDescription;
  const twitterTitle = meta?.twitterTitle || pageTitle;
  const twitterDescription = meta?.twitterDescription || pageDescription;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/*
          `keywords` is deliberately NOT emitted here. Layout wraps every route
          and mounts *after* <DefaultSeo>, which already emits a locale-correct
          default (`home:seo.default.keywords`). Because Helmet de-dupes meta by
          name and the last instance wins, a default here does not duplicate the
          tag — it silently overrides the localized one, which is how every
          Swedish page without its own keywords shipped an English string
          ("AI Scheduling, Home Care Automation, Digital Planning").
          DefaultSeo owns the site-wide default; PageSeo owns the per-page one.
        */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph meta tags */}
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:type" content="website" />

        {/* Twitter meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={twitterDescription} />

        {/* Canonical link */}
        <link rel="canonical" href={`${BASE_URL}${location.pathname}`} />
      </Helmet>

      {/*
        No Organization/WebSite JSON-LD here. Layout wraps every route and
        mounts *after* <DefaultSeo>, which already emits both — so emitting them
        again shipped two Organization and two WebSite nodes on every page, and
        Semrush read that as a structured-data error on 93 of 100 crawled pages.
        Unlike meta tags, Helmet does not de-dupe <script> children, so the
        second copy is additive rather than overriding. The copies here were
        also the worse ones: WebSiteSchema's default name is the English
        "Caire - AI-Powered Home Care Platform", which shipped on every Swedish
        page, while DefaultSeo's node is locale-correct — the same failure the
        `keywords` note above describes. DefaultSeo owns the site-wide entities;
        PageSeo owns the per-page ones.
      */}

      <div className="flex min-h-screen flex-col bg-caire-page text-caire-ink">
        <MainNav />
        <div className="pt-16">
          <AnnouncementBar />
        </div>
        <main
          id="marketing-content"
          tabIndex={-1}
          className="brand2-marketing-surface w-full flex-grow scroll-mt-20"
        >
          <EditorialNavigationLayout>{children}</EditorialNavigationLayout>
        </main>
        <Footer />
        <CookieConsent />
        <AskCaireLauncher />
      </div>
    </>
  );
};

export default Layout;
